import AnticipationFlow from '@components/anticipation-flow/anticipation-flow.component'
import ModalNfeConnection from '@components/modals/modal-nfe-connection/modal-nfe-connection.component'
import ModalSendNf from '@components/modals/modal-send-nf/modal-send-nf.component'
import ValuesControl from '@components/values-control/values-control.component'
import { NumbersHelper, PaymentStatus } from '@hub/shared'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './amount-receivable.component.scoped.scss'
import Footer from './components/footer/footer.component'
import MainContent from './components/main-content/main-content.component'
import { AmountReceivableProvider } from './hooks/use-amount-receivable-context/use-amount-receivable-context.hook'
import useAmountReceivable, { AmountReceivableProps } from './hooks/use-amount-receivable/use-amount-receivable.hook'

const AmountReceivable = ({ transactionObject, onChange, updateTransaction }: AmountReceivableProps) => {
  const numbersHelper = useMemo(() => new NumbersHelper(), [])
  const { t, i18n } = useTranslation('amountReceivable')
  const { state, setState } = useAmountReceivable()
  const [hideValue, setHideValue] = useState(true)
  const [openModalNfeConnection, setOpenModalNfeConnection] = useState(false)
  const [openModalSendNf, setOpenModalSendNf] = useState(false)
  const [transactionId, setTransactionId] = useState<string | undefined>('')

  const handleChange = useCallback(
    (value: boolean) => {
      setHideValue(value)

      if (onChange && typeof onChange === 'function') {
        onChange(value)
      }
    },
    [onChange]
  )
  const formatDate = useCallback((date?: string | Date, lang: string = 'pt-BR') => {
    const dateObj = date ? new Date(date) : new Date()
    return dateObj.toLocaleDateString(lang, { timeZone: 'UTC' })
  }, [])

  return (
    <AmountReceivableProvider value={{ state, setState, transactionObject }}>
      <div className="amount-receivable">
        <MainContent>
          <div className="display-flex justify-content-space-between">
            <h3 className="m-0">{t('title')}</h3>
            <ValuesControl valueChange={(value: boolean) => handleChange(value)} />
          </div>
          <h2 className="m-0">{hideValue ? '••••••' : numbersHelper.convertMoneyValue(transactionObject?.transaction?.grossValue || 0)}</h2>
          <MainContent.PaymentForecastLabel
            transactionObject={transactionObject}
            transactionId={transactionId}
            startAnticipationFlow={(transactionId) => setTransactionId(transactionId)}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: t('paymentForecast', {
                  date: formatDate(transactionObject?.transaction?.dueDate?.split('T')[0]),
                }),
              }}
            />
          </MainContent.PaymentForecastLabel>
          {transactionObject?.transaction?.paymentType === 'nf' && (
            <MainContent.SendTaxDocumentsButton
              openModalNfeConnection={() => setOpenModalNfeConnection(true)}
              openModalSendNf={() => setOpenModalSendNf(true)}
            />
          )}
        </MainContent>
        <Footer>
          <PaymentStatus
            paymentType={transactionObject?.transaction?.paymentType!}
            status={transactionObject?.transaction?.transactionStatus!}
            hasNf={!!transactionObject?.nfs?.length}
          />
        </Footer>
      </div>
      <ModalNfeConnection
        open={openModalNfeConnection}
        transactionObject={transactionObject}
        closeModal={() => setOpenModalNfeConnection(false)}
        updateTransaction={() => (updateTransaction && typeof updateTransaction === 'function' ? updateTransaction() : null)}
        setOpenModalSendNf={() => setOpenModalSendNf(true)}
      />
      {!transactionId ? (
        <ModalSendNf open={openModalSendNf} closeModal={() => setOpenModalSendNf(false)} transactionObject={transactionObject} />
      ) : null}
      <AnticipationFlow transactionId={transactionId} onCloseFlow={() => setTransactionId('')} />
    </AmountReceivableProvider>
  )
}

export default AmountReceivable
